
import { Vue, Component, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import OriginVhostRepositoryInterface, {
  OriginVhostConfigUpdateInterface,
  OriginVhostInterface,
} from 'src/services/Vhost/Origin/Interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import EditVhostDialog from '@/components/dialogs/vhost/origin/EditVhostDialog.vue';
import DeleteVhostDialog from '@/components/dialogs/vhost/origin/DeleteVhostDialog.vue';
import VhostPHPConfigDialog from '@/components/dialogs/vhost/origin/VhostPHPConfigDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'originVhostRepository',
    'serverRepository',
    'websiteRepository',
    'systemUserRepository',
  ],
  components: {
    EditVhostDialog,
    DeleteVhostDialog,
    VhostPHPConfigDialog,
  },
})
export default class AllVHosts extends Vue {
  private loading: boolean = true;
  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Server', value: 'serverId' },
    { text: 'Website', value: 'websiteId' },
    { text: 'System User', value: 'systemUserId' },
    { text: 'Aliases', value: 'aliases' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private originVhostRepository!: OriginVhostRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private systemUserRepository!: SystemUserRepositoryInterface;

  private vhosts: OriginVhostInterface[] = [];
  private totalVhostsCount: number = 0;

  private serversMap: { [key: string]: ServerInterface } = {};
  private websitesMap: { [key: number]: Website } = {};
  private systemUsersMap: { [key: number]: SystemUserInterface } = {};

  private originVhostStatusStrings: { [key: number]: string } = {
    0: 'In Progress',
    1: 'Done',
    2: 'Error',
    3: 'Active',
    4: 'Disabled',
  };

  private systemUsersPage: number = 1;

  private search: string = '';
  private actualSearch: string = '';

  private options: { page: number, itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public editVhostDialogData: {
    vhost: OriginVhostInterface,
    server: ServerInterface,
    website: Website,
    systemUser: SystemUserInterface
  } | false = false;
  private editVhostDialog: string = '';

  private deleteVhostDialog: string = '';
  private vhostForDeleteDialog: OriginVhostInterface | false = false;
  private domainForDeleteDialog: string | false = false;

  private vhostConfigDialog: string = '';
  private dataForConfigDialog: {
    vhost: OriginVhostInterface,
    server: ServerInterface,
    website: Website,
  } | false = false;


  private async created(): Promise<void> {
    await this.initialize();
    this.loading = false;
  }

  private async changePage(): Promise<void> {
    this.loading = true;
    await this.initialize();
  }

  private async searchForVhost(): Promise<void> {
    this.loading = true;
    this.options.page = 1;
    this.actualSearch = '';

    const server = Object.values(this.serversMap).find((server) => server.hostname === this.search);
    if (server) {
      this.actualSearch = server._id;
    }

    const website = Object.values(this.websitesMap).find((website) => website.domain === this.search);
    if (website) {
      this.actualSearch = String(website.id);
    }

    const systemUser = Object.values(this.systemUsersMap).find((systemUser) => systemUser.name === this.search);
    if (systemUser) {
      this.actualSearch = String(systemUser.id);
    }

    if (!this.actualSearch) {
      this.actualSearch = this.search;
    }

    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      await this.getVhosts();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getVhosts(): Promise<void> {
    const [vhosts, totalCount] = await this.originVhostRepository.searchVhosts(this.options.page, this.actualSearch);

    let serversIds: string[] = [];
    let websitesIds: number[] = [];
    let systemUsersIds: number[] = [];
    vhosts.forEach((vhost) => {
      serversIds.push(vhost.serverId);
      websitesIds.push(vhost.websiteId);
      systemUsersIds.push(vhost.systemUserId);
    });

    serversIds = serversIds.filter((serverId, index, self) => self.indexOf(serverId) === index);
    websitesIds = websitesIds.filter((websiteId, index, self) => self.indexOf(websiteId) === index);
    systemUsersIds = systemUsersIds.filter((systemUserId, index, self) => self.indexOf(systemUserId) === index);

    await Promise.all([
      this.getServers(serversIds),
      this.getWebsites(websitesIds),
      this.getSystemUsers(systemUsersIds),
    ]);

    this.vhosts = vhosts;
    this.totalVhostsCount = totalCount;
  }

  private async getServers(ids: string[]): Promise<void> {
    const servers = await this.serverRepository.getServersByIds(ids);
    servers.forEach((server) => this.serversMap[server._id] = server);
  }

  private async getWebsites(ids: number[]): Promise<void> {
    const websites = await this.websiteRepository.getWebsitesByIds(ids);
    websites.forEach((website) => this.websitesMap[website.id] = website);
  }

  private async getSystemUsers(ids: number[], users: SystemUserInterface[] = []): Promise<void> {
    const [systemUsers, totalCount] = await this.systemUserRepository
        .findSystemUsersByIds(this.systemUsersPage, ids);
    if (users.length) {
      systemUsers.push(...users);
    }

    const pages = Math.ceil(totalCount / this.systemUsersPage);
    if (this.systemUsersPage < pages) {
      this.systemUsersPage += 1;
      return this.getSystemUsers(ids, systemUsers);
    }

    systemUsers.forEach((systemUser) => this.systemUsersMap[systemUser.id] = systemUser);
  }

  private createVhost(): void {
    this.$router.push({ name: 'origin_vhost_create' });
  }

  private async editVhost(vhost: OriginVhostInterface): Promise<void> {
    try {
      await this.originVhostRepository.updateVhost(
          vhost.id,
          {
            aliases: vhost.aliases.map((vhostAlias) => vhostAlias.alias),
            php: {
              version: vhost.php.version,
            },
          },
      );
      this.$successDialog.show('Vhost Update Started!');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showDeleteDialog(vhost: OriginVhostInterface): void {
    this.deleteVhostDialog = 'delete-vhost-dialog';
    this.vhostForDeleteDialog = vhost;
    this.domainForDeleteDialog = this.websitesMap[vhost.websiteId].domain;
  }

  private async deleteVhost(vhost: OriginVhostInterface): Promise<void> {
    try {
      await this.originVhostRepository.deleteVhost(vhost.id);
      this.$successDialog.show('Vhost Delete Started!');
      this.vhosts = this.vhosts.filter((v) => v.id !== vhost.id);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private viewVhost(vhost: OriginVhostInterface): void {
    this.editVhostDialogData = {
      vhost,
      server: this.serversMap[vhost.serverId],
      website: this.websitesMap[vhost.websiteId],
      systemUser: this.systemUsersMap[vhost.systemUserId],
    };
    this.editVhostDialog = 'edit-vhost-dialog';
  }

  private viewVhostPHPConfig(vhost: OriginVhostInterface): void {
    this.dataForConfigDialog = {
      vhost,
      server: this.serversMap[vhost.serverId],
      website: this.websitesMap[vhost.websiteId],
    };
    this.vhostConfigDialog = 'vhost-PHP-config-dialog';
  }

  private async updateConfig(config: OriginVhostConfigUpdateInterface, vhostId: number): Promise<void> {
    try {
      await this.originVhostRepository.updatePHPConfig(vhostId, config);
      this.$successDialog.show('Vhost Config Update Started.');
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }
}
